import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables';
import { Permission } from '@Constants/Permission.g';
import { orderColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { registerInvoiceCreationNotification, useCreateInvoices } from '@Hooks/Billing';
import { useGetOrders } from '@Hooks/Orders';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { StartWorkflowButton } from '@Modules/AgenticOrchestrator/StartWorkflowButton';
import { MONTHLY_FAKTURA_KEY } from '@Modules/AgenticOrchestrator/workflowTools';
import { FrameEditOrder } from '@Modules/Orders';
import {
    Badge,
    Button,
    defaultIconSize,
    FloatingToolbar,
    HeadlessFrame,
    isNullOrEmpty,
    ITableProperties,
    MultiFrame,
    SingleSelect,
    useToasts,
    useTableRefetch,
    type ToolbarOptions,
} from '@synqo/frontend-component-library';
import { IconCircleCheck } from '@tabler/icons-react';
import { type ApiOrder, EnumFrontendLayoutSetting, EnumProcessState } from '@Model';
import { useCallback, useMemo, useState } from 'react';
import { type CreateInvoiceTableModes } from './CreateInvoiceTableModes';
import { IHasTableModes } from '@Hooks/useTableModes';

interface IFrameCreateInvoicesFromOrdersProps extends IHasTableModes<CreateInvoiceTableModes> {}

export function FrameCreateInvoicesFromOrders({
    tableModeValue,
    setTableMode,
    availableTableModes,
}: IFrameCreateInvoicesFromOrdersProps) {
    const hasPermission = useHasPermission();
    const { pushToast } = useToasts();

    const [previewedOrderId, setPreviewedOrderId] = useState<string>('');
    const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

    const { refetchRef, refetchTable } = useTableRefetch();
    useRemoteCacheInvalidation('orders', refetchTable);

    const { createInvoices, isInvoiceCreationPending } = useCreateInvoices();
    const loadApprovedOrders = useGetOrders(EnumProcessState.Approved);

    const startCreateInvoices = useCallback(() => {
        if (selectedOrderIds.length == 0) {
            pushToast({
                variant: 'error',
                title: 'Keine Rechnung',
                message: 'Es wurden keine Rechnungen ausgewählt.',
            });
            return undefined;
        }

        setSelectedOrderIds([]);
        const creationStartedId = pushToast({
            variant: 'info',
            title: 'Rechnungserzeugung',
            message: 'Erzeugung von Rechnungen wurde gestartet.',
        });
        registerInvoiceCreationNotification(creationStartedId);

        return createInvoices(selectedOrderIds);
    }, [selectedOrderIds, pushToast, setSelectedOrderIds, createInvoices]);

    const closeOrderAside = useCallback(() => {
        setPreviewedOrderId('');
    }, [setPreviewedOrderId]);

    const selectOrder = useCallback(
        (order: ApiOrder) => {
            setPreviewedOrderId(order.id);
            return true;
        },
        [setPreviewedOrderId],
    );

    const rowFlags = useMemo<ITableProperties<ApiOrder>['rowFlags']>(
        () =>
            previewedOrderId
                ? {
                      [previewedOrderId]: { appearance: 'highlighted' },
                  }
                : undefined,
        [previewedOrderId],
    );

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            <StartWorkflowButton
                key="startMonthlyFaktura"
                definitionKey={MONTHLY_FAKTURA_KEY}
                label="Monatliche Faktura starten"
            />,
            <SingleSelect<CreateInvoiceTableModes>
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
        ],
        [availableTableModes, tableModeValue, setTableMode],
    );

    return (
        <MultiFrame>
            <HeadlessFrame>
                <TableWithStoredSettingsAndLayoutSettings<ApiOrder>
                    layoutSettingsContext={EnumFrontendLayoutSetting.CreateInvoicesTableLayout}
                    name="CreateInvoicesList"
                    columns={orderColumns}
                    loader={loadApprovedOrders}
                    refetchRef={refetchRef}
                    onSelect={selectOrder}
                    objectSelector={{
                        selectedObjectIds: selectedOrderIds,
                        setSelectedObjectIds: setSelectedOrderIds,
                    }}
                    canCreate={
                        hasPermission(Permission.BillingInvoicesCreate) &&
                        hasPermission(Permission.AuftragsbearbeitungOrdersUpdate)
                    }
                    rowFlags={rowFlags}
                    toolbarElements={toolbarElements}
                    fullSize
                />
                {selectedOrderIds.length > 0 && (
                    <FloatingToolbar>
                        <Badge variant="success" type="input">
                            {selectedOrderIds.length}
                        </Badge>

                        {/* <Button label="selectAll" appearance="transparent" onClick={() => {}}>Alle auswählen</Button> Not implementent see DEVCLD-1076 */}

                        <Button variant="success" onClick={startCreateInvoices} locked={isInvoiceCreationPending}>
                            <IconCircleCheck size={defaultIconSize} />
                            &nbsp;Rechnungen erstellen
                        </Button>
                    </FloatingToolbar>
                )}
            </HeadlessFrame>
            {!isNullOrEmpty(previewedOrderId) && (
                <FrameEditOrder
                    key={previewedOrderId}
                    primaryKey={previewedOrderId}
                    events={{
                        onClose: closeOrderAside,
                        onBack: undefined,
                        onAdd: undefined,
                    }}
                />
            )}
        </MultiFrame>
    );
}
